import { isProdEnv } from 'utils/utilities';

// Regula Document Reader settings.

export default {
    document: {
        // → `recognizerProcessParam.processParam`, the live-camera processing path. The still-image
        // path has its own parameter object and takes only the scenario from this one.
        documentServiceUrl: process.env.REACT_APP_REGULA_DOCUMENT_SERVICE_URL,
        // Ours, not the SDK's. `processParam.timeout` below is enforced by the frame-by-frame
        // recognition, which never starts where the user takes the photos, so that camera is bounded
        // here instead — otherwise only the shutter or the close button would ever end it. Longer than
        // the automatic timeout because getting the document out and lined up is the user's job there.
        manualCaptureTimeout: 90000,
        processParam: {
            env: isProdEnv() ? 'Production' : 'Development',
            scenario: 'Locate',
            doublePageSpread: false,
            multipageProcessing: true, // front + back of an ID1 card in one camera session
            strictImageQuality: true, // makes the checks below a hard gate, not advisory
            timeout: 45000,
            returnUncroppedImage: true,
            // A two-sided capture must not race a first-detect countdown: the user needs time to
            // flip the card after the front is detected.
            timeoutFromFirstDetect: 0,
            imageQa: {
                focusCheck: true,
                glaresCheck: true,
                occlusionCheck: true, //For cases where parts of a document are covered by fingers, hands, or other objects.
                occlusionCheckParams: {
                    maxOcclusionPart: 0.005,
                },
                dpiThreshold: 150,
                angleThreshold: 5,
                expectedPass: [
                    'dpiThreshold',
                    'glaresCheck',
                    'focusCheck',
                    'occlusionCheck',
                    'IQC_Perspective',
                ],
            },
        },
        /**
         * The capture frame the component draws over the camera feed, whose two colors say which
         * state it is in: the company's primary once the document is found, its secondary while the
         * camera is still looking. `buildCaptureSettings` reads the two colors off the theme; the
         * rounding below is not themed.
         *
         * These are the fallbacks for a company with no theme of its own, and they are the same two
         * the rest of the app falls back to — see `ContentArea`.
         */
        frame: {
            foundColor: '#154683',
            searchingColor: '#1ba1db',
            cornerRadius: 15,
        },
        // → <document-reader>.settings
        settings: {
            /**
             * Pins which entry of the translations dictionary is read; left to the element it would
             * follow the browser, and a French one would fall back to the vendor's own copy.
             *
             * The full tag, not `'en'`: the element resolves a locale by looking the value up in its
             * dictionary as given, and every key it ships is a full tag. A two-letter value matches
             * nothing there and drops the copy back to the label names.
             */
            locale: 'en-us',
            cameraMode: 'environment', // CameraFacingMode.environment — rear camera
            startScreen: false, // we own the pre-capture screens
            mirrorType: 'noMirror', // MirrorType.noMirror
            // A timed-out capture has to end as a timeout and nothing else. Left `true` (the
            // default), a timeout instead parks the component waiting for a `finalizePackage()` we
            // must not make: a partial capture may hold only the front of a two-sided card, and
            // finalizing it would spend a paid transaction on an incomplete document.
            finalizeOnTimeout: false,
            videoCaptureMotionControl: true, // reject shaky frames
            newLayout: true,
            // Vertical placement of the frame: 0 against the top controls, 1 centred, 2 bottom. Set
            // explicitly because the default is 0, which leaves a portrait frame under the close
            // button. Moves the frame and the video together; CSS cannot.
            cameraFrameVerticalPositionMultiplier: 1,
            uploadFileButton: false, // the camera is the only input we accept
            changeCameraButton: false,
            // Offered on the "flip the document" prompt, and on by default. Pressing it reports the
            // capture as a success carrying only the side already taken, which is indistinguishable
            // here from a document that is genuinely complete — so the flow would finalize, spend the
            // attempt, and have the backend reject it for the missing side. The close button is the
            // way out of that prompt, and it costs neither.
            skipButton: false,
            closeButton: true, // the user's way out of the camera
            copyright: false,
            resolution: { width: 1920, height: 1080 },
        },
        /**
         * What the auto/manual toggle switches, merged over `settings` for the mode in play.
         *
         * `manual` waits for the user: the camera opens without scanning anything, and the press is
         * what starts the recognition. The engine still chooses the frame it is satisfied with, so
         * the button begins a scan rather than taking a photo outright.
         *
         * One page per session either way, so the flow keeps opening the transaction and advancing the
         * pages; quality and the capture timeout are the engine's again once scanning starts.
         *
         * `multipageProcessing` stays on in both: it is what makes the engine report a page count at
         * all, and manual mode reads that count to decide whether another page is needed.
         */
        modeSettings: {
            auto: {
                manualMultipageMode: false,
                captureMode: 'auto', // CaptureMode.auto — scanning starts with the camera
                captureButton: false, // nothing to press
                /**
                 * How long the engine holds between the two sides. Recognition is stopped for the
                 * whole of it, so nothing is missed while the user turns the document over — and it
                 * is the only lever on that pause, which the vendor otherwise spends on an animation
                 * of its own that comes and goes before it has been read.
                 *
                 * These two bracket a fixed 1.5s animation the vendor runs between them, so the
                 * window the user actually gets is the sum of all three. Manual mode ignores both:
                 * there, the between-pages screen is a screen and waits for a press.
                 */
                nextPageAnimationStartDelay: 3000,
                nextPageAnimationEndDelay: 3000,
            },
            manual: {
                manualMultipageMode: true, // the flow opens the transaction and advances the pages
                captureMode: 'captureVideo', // CaptureMode.captureVideo — the press starts the scan
                captureButton: true, // shown regardless in this mode; stated so the intent is readable
            },
        },
    },
};
